import { useMutation } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Upload,
  XCircle,
} from "lucide-react";
import { useRef, useState } from "react";

import { Button, Card, CardHeader, ErrorState, Pill, ToggleField } from "@/components/ui";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/cn";
import {
  catalogueRepository,
  saveBlob,
  type ImportJob,
  type ImportRow,
} from "@/repositories/catalogueRepository";

import { useImportLimits, useRefreshCatalogue } from "./useCatalogueData";

/**
 * Bulk import, in three steps.
 *
 *   choose a file  →  read what is wrong with it  →  import the good rows
 *
 * The middle step is the whole feature. A shopkeeper's stock list will have
 * problems in it — a blank price, the same barcode twice, a category spelled two
 * ways — and an import that either refuses the whole file or silently swallows
 * the bad rows is useless in both directions. This shows them exactly what will
 * happen, and only then offers the button.
 *
 * Nothing is written to the catalogue until "Import" is pressed. The upload step
 * calls an endpoint that parses and judges and creates no products at all.
 */
export function ImportWizard({ onDone }: { onDone: () => void }) {
  const [job, setJob] = useState<ImportJob | null>(null);

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onDone}
        className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900"
      >
        <ArrowLeft className="size-4" aria-hidden />
        Back to products
      </button>

      {job === null ? (
        <ChooseFileStep onChecked={setJob} />
      ) : job.status === "COMPLETED" ? (
        <DoneStep job={job} onFinish={onDone} onAnother={() => setJob(null)} />
      ) : (
        <ReviewStep job={job} onCommitted={setJob} onStartOver={() => setJob(null)} />
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// 1. Choose a file
// ═════════════════════════════════════════════════════════════════════════════
function ChooseFileStep({ onChecked }: { onChecked: (job: ImportJob) => void }) {
  const limits = useImportLimits();
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [createCategories, setCreateCategories] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [dragging, setDragging] = useState(false);

  const check = useMutation({
    mutationFn: (chosen: File) =>
      catalogueRepository.checkImport(chosen, {
        createMissingCategories: createCategories,
        updateExisting,
      }),
    onSuccess: onChecked,
  });

  const template = useMutation({
    mutationFn: catalogueRepository.downloadTemplate,
    onSuccess: (blob) => saveBlob(blob, "smartspaza-product-template.xlsx"),
  });

  return (
    <Card className="space-y-5">
      <CardHeader
        title="Import products from a file"
        subtitle="Nothing is added until you have seen the check report and confirmed it."
        action={
          <Button
            variant="secondary"
            icon={<Download className="size-4" aria-hidden />}
            onClick={() => template.mutate()}
            disabled={template.isPending}
          >
            Download the template
          </Button>
        }
      />

      {/* Drag-and-drop is a convenience layered over a real file input. The
          button inside is the keyboard and screen-reader path and carries the
          label, so this container needs no role of its own. */}
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const dropped = event.dataTransfer.files[0];
          if (dropped) setFile(dropped);
        }}
        className={cn(
          "rounded-card border-2 border-dashed px-6 py-10 text-center transition-colors",
          dragging ? "border-brand-500 bg-brand-50" : "border-line",
        )}
      >
        <FileSpreadsheet className="mx-auto size-8 text-ink-400" aria-hidden />

        {file ? (
          <>
            <p className="mt-3 font-medium text-ink-900">{file.name}</p>
            <p className="text-sm text-ink-500">{(file.size / 1024).toFixed(0)} KB</p>
          </>
        ) : (
          <>
            <p className="mt-3 font-medium text-ink-900">Drop your spreadsheet here</p>
            <p className="text-sm text-ink-500">
              CSV or Excel, up to {limits.data ? limits.data.max_rows : 1000} products.
            </p>
          </>
        )}

        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xlsm,text/csv"
          className="sr-only"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
        <Button className="mt-4" variant="secondary" onClick={() => inputRef.current?.click()}>
          {file ? "Choose a different file" : "Choose a file"}
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <ToggleField
          label="Create categories that do not exist yet"
          hint="Off means rows naming an unknown category are rejected."
          checked={createCategories}
          onChange={setCreateCategories}
        />
        <ToggleField
          label="Update products I already have"
          hint="Matches on barcode or SKU and changes the price. Off means those rows are rejected as duplicates."
          checked={updateExisting}
          onChange={setUpdateExisting}
        />
      </div>

      {check.isError && <UploadError error={check.error} />}

      <div className="flex justify-end">
        <Button
          variant="primary"
          icon={<Upload className="size-4" aria-hidden />}
          disabled={!file || check.isPending}
          onClick={() => file && check.mutate(file)}
        >
          {check.isPending ? "Checking the file…" : "Check this file"}
        </Button>
      </div>

      {limits.data && <ColumnGuide columns={limits.data.columns} />}
    </Card>
  );
}

function UploadError({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? error.detail || error.message
      : "The file could not be sent. Check your connection and try again.";
  return <ErrorState title="That file could not be read" description={message} />;
}

function ColumnGuide({
  columns,
}: {
  columns: { key: string; heading: string; required: boolean; help: string }[];
}) {
  return (
    <details className="rounded-card border border-line px-4 py-3">
      <summary className="cursor-pointer text-sm font-medium text-ink-900">
        What can my file contain?
      </summary>
      <p className="mt-2 text-sm text-ink-500">
        Your own column names are fine — “Price”, “Sell Price” and “Retail” all work. Prices may be
        written 12.50 or 12,50.
      </p>
      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {columns.map((column) => (
          <div key={column.key}>
            <dt className="text-sm font-medium text-ink-900">
              {column.heading}
              {column.required && (
                <span className="ml-1.5 text-xs font-normal text-danger-700">required</span>
              )}
            </dt>
            {column.help && <dd className="text-xs text-ink-500">{column.help}</dd>}
          </div>
        ))}
      </dl>
    </details>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// 2. Review
// ═════════════════════════════════════════════════════════════════════════════
function ReviewStep({
  job,
  onCommitted,
  onStartOver,
}: {
  job: ImportJob;
  onCommitted: (job: ImportJob) => void;
  onStartOver: () => void;
}) {
  const refresh = useRefreshCatalogue();

  const commit = useMutation({
    mutationFn: () => catalogueRepository.commitImport(job.id),
    onSuccess: (updated) => {
      refresh();
      onCommitted(updated);
    },
  });

  const report = useMutation({
    mutationFn: () => catalogueRepository.downloadReport(job.id),
    onSuccess: (blob) => saveBlob(blob, `check-report-${job.original_filename}.xlsx`),
  });

  return (
    <Card className="space-y-5">
      <CardHeader
        title={`We checked ${job.original_filename}`}
        subtitle={
          job.error_rows > 0
            ? "Rows with a problem will be skipped. Everything else will be imported."
            : "Every row can be imported."
        }
        action={
          <Button
            variant="secondary"
            icon={<Download className="size-4" aria-hidden />}
            onClick={() => report.mutate()}
            disabled={report.isPending}
          >
            Download the report
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tally label="Rows read" value={job.total_rows} />
        <Tally label="Ready" value={job.valid_rows} tone="success" />
        <Tally label="With warnings" value={job.warning_rows} tone="warn" />
        <Tally label="Cannot import" value={job.error_rows} tone="danger" />
      </div>

      {job.failure_reason && (
        <p className="rounded-card border border-warn-500/30 bg-warn-50 px-4 py-3 text-sm text-warn-700">
          {job.failure_reason}
        </p>
      )}

      {job.problem_rows && job.problem_rows.length > 0 && (
        <ProblemList rows={job.problem_rows} total={job.error_rows} />
      )}

      {job.preview_rows && job.preview_rows.length > 0 && <PreviewList rows={job.preview_rows} />}

      {commit.isError && (
        <ErrorState
          title="The import did not run"
          description={commit.error instanceof ApiError ? commit.error.detail : "Please try again."}
        />
      )}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onStartOver} disabled={commit.isPending}>
          Choose a different file
        </Button>
        <Button
          variant="primary"
          disabled={job.importable_rows === 0 || commit.isPending}
          onClick={() => commit.mutate()}
        >
          {commit.isPending
            ? "Importing…"
            : `Import ${job.importable_rows} ${job.importable_rows === 1 ? "product" : "products"}`}
        </Button>
      </div>
    </Card>
  );
}

function Tally({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "success" | "warn" | "danger";
}) {
  const toneClass = {
    neutral: "text-ink-900",
    success: "text-brand-700",
    warn: "text-warn-700",
    danger: "text-danger-700",
  }[tone];

  return (
    <div className="rounded-card border border-line px-4 py-3">
      <div className={cn("text-kpi tabular-nums", toneClass)}>{value}</div>
      <div className="text-xs text-ink-500">{label}</div>
    </div>
  );
}

function ProblemList({ rows, total }: { rows: ImportRow[]; total: number }) {
  return (
    <div className="rounded-card border border-danger-500/30">
      <div className="flex items-center gap-2 border-b border-danger-500/30 bg-danger-50 px-4 py-2.5">
        <XCircle className="size-4 text-danger-700" aria-hidden />
        <h3 className="text-sm font-semibold text-danger-700">
          {total} {total === 1 ? "row cannot be imported" : "rows cannot be imported"}
        </h3>
      </div>
      <ul className="divide-y divide-line">
        {rows.map((row) => (
          <li key={row.id} className="flex gap-3 px-4 py-2.5">
            {/* The spreadsheet's own row number, so "row 14" means row 14 when
                they open the file. */}
            <span className="mt-0.5 font-mono text-xs text-ink-400">Row {row.row_number}</span>
            <div className="min-w-0">
              <p className="truncate text-sm text-ink-900">
                {row.data.name || <span className="italic text-ink-400">no name</span>}
              </p>
              <ul className="text-xs text-danger-700">
                {row.issues
                  .filter((issue) => issue.severity === "error")
                  .map((issue) => (
                    <li key={`${issue.field}-${issue.code}`}>{issue.message}</li>
                  ))}
              </ul>
            </div>
          </li>
        ))}
      </ul>
      {total > rows.length && (
        <p className="border-t border-line px-4 py-2.5 text-xs text-ink-500">
          Showing the first {rows.length}. Download the report to see them all.
        </p>
      )}
    </div>
  );
}

function PreviewList({ rows }: { rows: ImportRow[] }) {
  return (
    <div className="rounded-card border border-line">
      <h3 className="border-b border-line px-4 py-2.5 text-sm font-semibold text-ink-900">
        The first {rows.length} rows, as we read them
      </h3>
      <ul className="divide-y divide-line">
        {rows.map((row) => (
          <li key={row.id} className="flex items-start gap-3 px-4 py-2.5">
            <RowIcon status={row.status} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm text-ink-900">
                {row.data.name || <span className="italic text-ink-400">no name</span>}
              </p>
              {row.issues.length > 0 && (
                <p className="text-xs text-ink-500">
                  {row.issues.map((issue) => issue.message).join(" ")}
                </p>
              )}
            </div>
            {row.data.selling_price && (
              <span className="tabular-nums text-sm text-ink-900">
                R{Number(row.data.selling_price).toFixed(2)}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RowIcon({ status }: { status: ImportRow["status"] }) {
  if (status === "ERROR" || status === "FAILED") {
    return (
      <XCircle className="mt-0.5 size-4 shrink-0 text-danger-700" aria-label="Cannot import" />
    );
  }
  if (status === "WARNING") {
    return (
      <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn-700" aria-label="Check this one" />
    );
  }
  return <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-brand-600" aria-label="Ready" />;
}

// ═════════════════════════════════════════════════════════════════════════════
// 3. Done
// ═════════════════════════════════════════════════════════════════════════════
function DoneStep({
  job,
  onFinish,
  onAnother,
}: {
  job: ImportJob;
  onFinish: () => void;
  onAnother: () => void;
}) {
  const skipped = job.error_rows;

  return (
    <Card className="space-y-5 text-center">
      <div>
        <CheckCircle2 className="mx-auto size-10 text-brand-600" aria-hidden />
        <h2 className="mt-3 text-lg font-semibold text-ink-900">
          {job.imported_rows} {job.imported_rows === 1 ? "product" : "products"} added
        </h2>
        {(skipped > 0 || job.failed_rows > 0) && (
          <p className="mt-1.5 text-sm text-ink-500">
            {skipped > 0 && `${skipped} skipped because of a problem. `}
            {job.failed_rows > 0 && `${job.failed_rows} could not be saved. `}
            Download the report to see which, fix them, and import that file again.
          </p>
        )}
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        <Button
          variant="secondary"
          icon={<Download className="size-4" aria-hidden />}
          onClick={() => {
            void catalogueRepository
              .downloadReport(job.id)
              .then((blob) => saveBlob(blob, `import-report-${job.original_filename}.xlsx`));
          }}
        >
          Download the report
        </Button>
        <Button variant="secondary" onClick={onAnother}>
          Import another file
        </Button>
        <Button variant="primary" onClick={onFinish}>
          See my products
        </Button>
      </div>

      {job.failed_rows > 0 && (
        <p className="text-xs text-ink-500">
          <Pill tone="warn">Note</Pill> The rows that failed were left out; the rest were saved.
          Nothing was undone.
        </p>
      )}
    </Card>
  );
}
